import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Loader } from 'lucide-react';

export default function DeleteAccountDialog({ open, onOpenChange, onConfirm, userEmail }) {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  const canDelete = confirmText.toLowerCase() === 'supprimer';

  const handleConfirm = async () => {
    if (!canDelete) return;
    setIsDeleting(true);
    try {
      await onConfirm();
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (isDeleting) return;
    setConfirmText('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-[#13151D] border-2 border-red-600 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-400 font-bold uppercase flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Supprimer mon compte
          </DialogTitle>
          <DialogDescription className="text-[#B3B6C2] space-y-3 pt-2">
            <p className="font-semibold text-white">Cette action est irréversible.</p>
            <p>Toutes vos données seront définitivement supprimées :</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Votre profil et statistiques</li>
              <li>Vos terrains capturés</li>
              <li>Votre historique de duels</li>
              <li>Vos highlights vidéos</li>
              <li>Vos chances de tirage</li>
              <li>Votre progression XP</li>
            </ul>
            <p className="text-red-400 font-semibold text-sm pt-2">
              ⚠️ Aucune récupération possible après suppression
            </p>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          <div>
            <p className="text-sm text-[#B3B6C2] mb-2">
              Pour confirmer, tapez <span className="font-bold text-white">SUPPRIMER</span>
            </p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Tapez SUPPRIMER"
              className="bg-[#0A0B10] border-red-600/30 text-white"
              disabled={isDeleting}
            />
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 border-[#B3B6C2] text-[#B3B6C2] hover:bg-[#1B1E28]"
              onClick={handleClose}
              disabled={isDeleting}
            >
              Annuler
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-50"
              onClick={handleConfirm}
              disabled={!canDelete || isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                'Supprimer définitivement'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}